import React, { useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  LayoutChangeEvent,
  Platform,
  Modal,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { Divider, IconButton, Tooltip } from "react-native-paper";
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLabel,
  VictoryVoronoiContainer,
} from "victory-native";
import ChartStyles from "./chartStyles";
import ChartExportDialog from "./ChartExportDialog";
import { runExport } from "./ChartExport";
import { ExportLineChartSvg } from "./victory/LineChartExportable";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineDataset {
  data?: (number | null | undefined)[];
  color?: ((opacity: number) => string) | string;
  strokeWidth?: number;
}

interface LineChartData {
  labels?: string[];
  datasets?: LineDataset[];
}

interface LegendItemInput {
  name?: string;
}

interface LineChartScreenProps {
  label: string[];
  data: LineChartData | null | undefined;
  ChartTitle: string;
  subTitle?: string;
  legend?: LegendItemInput[];
  yAxisLabel?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Safely resolve a dataset color — handles function, string, or missing */
const resolveColor = (dataset: LineDataset | undefined, fallback = "#4BB75F"): string => {
  if (!dataset) return fallback;
  if (typeof dataset.color === "function") return dataset.color(1);
  if (typeof dataset.color === "string") return dataset.color;
  return fallback;
};

// ─── Fullscreen styles ────────────────────────────────────────────────────────

const fsStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.55)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    backgroundColor: "#F9FCFF",
    borderRadius: 16,
    width: "100%",
    maxWidth: 1200,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  headerIcon: {
    backgroundColor: "#102B27",
    borderRadius: 8,
    height: 44,
    width: 44,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  headerTitles: {
    flex: 1,
  },
  headerTitle: {
    color: "#102B27",
    fontWeight: "bold",
    fontSize: 16,
  },
  headerSubTitle: {
    color: "#102B27",
    fontSize: 12,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  legendRow: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
  },
  chartArea: {
    padding: 16,
    minHeight: 480,
  },
});

// ─── Component ────────────────────────────────────────────────────────────────

export default function LineChartScreen(props: LineChartScreenProps) {
  const [showFull, setShowFull] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [legendPopoverVisible, setLegendPopoverVisible] = useState(false);
  const [fullLegendPopoverVisible, setFullLegendPopoverVisible] = useState(false);
  const [fsWidth, setFsWidth] = useState(0);

  const safeBase = (props.ChartTitle || "Chart").replace(/[\\/:*?"<>|]+/g, "-").trim();
  const exportBaseId = `${safeBase}-line`;
  const exportRootId = `${exportBaseId}-export`;
  const exportSvgId = `${exportRootId}-root`;

  // ── Data transforms ─────────────────────────────────────────────────────────

  /** Top-5 slice — used by the inline card chart and the download export */
  const transformedDatasets = useMemo(() => {
    return (props.data?.datasets ?? []).map((dataset, index) => {
      const top5Data = (dataset.data ?? []).slice(-5);
      const lbls = (props.data?.labels ?? []).slice(-5);
      return {
        data: top5Data.map((value, dataIndex) => ({
          x: lbls[dataIndex],
          y: Number(value) || 0,
          key: props.legend?.[index]?.name,
        })),
        strokeWidth: dataset.strokeWidth,
      };
    });
  }, [props.data, props.legend]);

  /** All data — fullscreen modal only */
  const transformedDatasetsAll = useMemo(() => {
    return (props.data?.datasets ?? []).map((dataset, index) => {
      const allData = dataset.data ?? [];
      return {
        data: allData.map((value, dataIndex) => ({
          x: props.data?.labels?.[dataIndex],
          y: Number(value) || 0,
          key: props.legend?.[index]?.name,
        })),
        strokeWidth: dataset.strokeWidth,
      };
    });
  }, [props.data, props.legend]);

  const hasData = (props.data?.datasets?.length ?? 0) > 0;

  const onChartLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const legendItems = useMemo(() => {
    return (props.legend ?? []).map((legendItem, index) => ({
      name: legendItem?.name ?? `Series ${index + 1}`,
      color: resolveColor(props.data?.datasets?.[index]),
    }));
  }, [props.legend, props.data]);

  const exportRowsWithLegend = useMemo(
    () =>
      transformedDatasets.flatMap((ds, i) =>
        ds.data.map((d) => ({
          Period: String(d.x ?? ""),
          Series: props.legend?.[i]?.name ?? `Series ${i + 1}`,
          Value: Number(d.y ?? 0),
        }))
      ),
    [transformedDatasets, props.legend]
  );

  /** Safe y-max for fullscreen domain — guards against empty-array spread */
  const fsYMax = useMemo(() => {
    const allY = transformedDatasetsAll.flatMap((ds) =>
      ds.data.map((d) => Number(d.y) || 0)
    );
    return allY.length ? Math.max(...allY) + 10 : 10;
  }, [transformedDatasetsAll]);

  // ── Fullscreen sizing / animation ────────────────────────────────────────────

  /** Max data points across all series — drives dynamic chart width */
  const maxPoints = transformedDatasetsAll.reduce(
    (max, ds) => Math.max(max, ds.data.length),
    1
  );

  /**
   * 60px per point ensures tick labels don't crowd; the chart scrolls
   * horizontally when this exceeds the measured container width.
   */
  const fsChartWidth = fsWidth > 0 ? Math.max(fsWidth, maxPoints * 60) : 0;

  /**
   * Disable Victory animation when there are many total points — it would
   * block the UI thread on mobile for large datasets (Rule 3).
   */
  const totalFsPoints = transformedDatasetsAll.reduce(
    (sum, ds) => sum + ds.data.length,
    0
  );
  const fsAnimate = totalFsPoints <= 30;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <View>
      <View style={ChartStyles.style}>
        <View
          style={[ChartStyles.container, ChartStyles.box2]}
          onLayout={onChartLayout}
        >
          {/* ── Header ── */}
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              width: containerWidth ?? undefined,
              justifyContent: "center",
            }}
          >
            <View>
              <TouchableOpacity style={ChartStyles.TitleIconHolder}>
                <IconButton icon="chart-bell-curve" iconColor={"#fff"} size={30} />
              </TouchableOpacity>
            </View>

            <View style={ChartStyles.chartTitleContainer}>
              <View>
                <Text style={[ChartStyles.chartTitle]}>{props.ChartTitle}</Text>
              </View>
              <View>
                <Text style={[ChartStyles.chartSubTitle]}>{props.subTitle}</Text>
              </View>
            </View>

            {Platform.OS === "web" ? (
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <View style={ChartStyles.fullScreenIconContainer}>
                  <Tooltip title="Download">
                    <IconButton
                      icon="download"
                      iconColor={ChartStyles.chartTitle.color}
                      size={20}
                      onPress={() => setDownloadOpen(true)}
                    />
                  </Tooltip>
                </View>
                <View style={ChartStyles.fullScreenIconContainer}>
                  <Tooltip title="View in Full Screen">
                    <IconButton
                      icon="fullscreen"
                      iconColor={ChartStyles.chartTitle.color}
                      size={20}
                      onPress={() => setShowFull(true)}
                    />
                  </Tooltip>
                </View>
              </View>
            ) : null}
          </View>

          <Divider />

          {/* ── Inline chart (top-5 window) ── */}
          <ScrollView
            style={{ backgroundColor: "#FFFFFF" }}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ justifyContent: "center" }}
          >
            <View style={{ paddingLeft: 5, paddingRight: 5 }}>
              {hasData ? (
                <VictoryChart
                  height={316}
                  padding={{ top: 20, bottom: 60, left: props.yAxisLabel ? 72 : 50, right: 20 }}
                  containerComponent={
                    <VictoryVoronoiContainer
                      labels={({ datum }) => `${datum.x}, ${datum.y}`}
                    />
                  }
                  domainPadding={{ x: 25, y: 20 }}
                >
                  <VictoryGroup style={{ data: { fillOpacity: 0.3 } }}>
                    {transformedDatasets.map((dataset, index) => {
                      const color = resolveColor(props.data?.datasets?.[index]);
                      return (
                        <VictoryArea
                          key={index}
                          interpolation="natural"
                          data={dataset.data}
                          animate={{ duration: 1500, onLoad: { duration: 1500 } }}
                          style={{ data: { stroke: color, fill: color } }}
                        />
                      );
                    })}
                  </VictoryGroup>

                  <VictoryAxis
                    style={{
                      tickLabels: { fontSize: 10, padding: 10, angle: -45, textAnchor: "end" },
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    label={props.yAxisLabel}
                    style={{ tickLabels: { fontSize: 10, padding: 5 }, axisLabel: { fontSize: 11, padding: 42 } }}
                  />
                </VictoryChart>
              ) : (
                <VictoryChart height={316} domainPadding={{ x: 25 }}>
                  <VictoryGroup style={{ data: { fillOpacity: 0.3 } }}>
                    <VictoryArea
                      data={[{ x: "Data", y: 0 }]}
                      style={{ data: { stroke: "gray", fill: "lightgray" } }}
                    />
                  </VictoryGroup>
                  <VictoryAxis
                    style={{
                      tickLabels: { fontSize: 10, padding: 10, angle: -45, textAnchor: "end" },
                    }}
                  />
                  <VictoryAxis dependentAxis style={{ tickLabels: { fontSize: 10, padding: 5 } }} />
                </VictoryChart>
              )}
            </View>
          </ScrollView>

          {/* ── Legend row ── */}
          <View style={{ width: containerWidth ?? undefined, height: 44, flexDirection: "row", alignItems: "center" }}>
            {hasData ? (
              <>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ alignItems: "center", paddingHorizontal: 10 }}
                  style={{ flex: 1 }}
                >
                  {legendItems.map((it, idx) => (
                    <View
                      key={`${it.name}-${idx}`}
                      style={{ flexDirection: "row", alignItems: "center", marginHorizontal: 8 }}
                    >
                      <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: it.color, marginRight: 6 }} />
                      <Text style={{ color: ChartStyles.chartTitle.color, fontSize: 12 }} numberOfLines={1}>
                        {it.name}
                      </Text>
                    </View>
                  ))}
                </ScrollView>

                <TouchableOpacity
                  onPress={() => setLegendPopoverVisible((v) => !v)}
                  style={{ paddingHorizontal: 6, height: 44, justifyContent: "center" }}
                >
                  <IconButton
                    icon={legendPopoverVisible ? "close-circle-outline" : "format-list-bulleted"}
                    size={16}
                    iconColor={ChartStyles.chartTitle.color}
                    style={{ margin: 0 }}
                  />
                </TouchableOpacity>

                {legendPopoverVisible && (
                  <>
                    <TouchableOpacity
                      style={{ position: "absolute", top: -9999, bottom: -9999, left: -9999, right: -9999, zIndex: 98 }}
                      onPress={() => setLegendPopoverVisible(false)}
                      activeOpacity={1}
                    />
                    <View
                      style={{
                        position: "absolute",
                        bottom: 44,
                        right: 8,
                        backgroundColor: "#fff",
                        borderRadius: 8,
                        paddingVertical: 10,
                        paddingHorizontal: 14,
                        shadowColor: "rgba(0,0,0,0.25)",
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 1,
                        shadowRadius: 10,
                        elevation: 12,
                        zIndex: 99,
                        minWidth: 160,
                      }}
                    >
                      {legendItems.map((it, idx) => (
                        <View key={`popover-${it.name}-${idx}`} style={{ flexDirection: "row", alignItems: "center", marginVertical: 4 }}>
                          <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: it.color, marginRight: 8 }} />
                          <Text style={{ color: ChartStyles.chartTitle.color, fontSize: 12 }}>{it.name}</Text>
                        </View>
                      ))}
                    </View>
                  </>
                )}
              </>
            ) : (
              <Text style={{ textAlign: "center", flex: 1 }}>No data available to display</Text>
            )}
          </View>
        </View>
      </View>

      {/* ── Off-screen SVG for PNG/SVG download (web only, uses top-5 data) ── */}
      {Platform.OS === "web" && (
        <View
          style={{
            position: "absolute",
            left: -100000,
            top: 0,
            overflow: "visible",
            width: Math.max(900, containerWidth ?? 900),
          }}
        >
          <ExportLineChartSvg
            chartId={exportSvgId}
            width={Math.max(900, containerWidth ?? 900)}
            datasets={transformedDatasets.map((ds, i) => ({
              data: ds.data,
              color: resolveColor(props.data?.datasets?.[i]),
            }))}
            legendItems={legendItems.map((l) => ({ label: l.name, color: l.color }))}
            yAxisLabel={props.yAxisLabel}
          />
        </View>
      )}

      {/* ── Export dialog ── */}
      {Platform.OS === "web" && (
        <ChartExportDialog
          open={downloadOpen}
          title={`Download: ${props.ChartTitle}`}
          hasSupportingData={exportRowsWithLegend.length > 0}
          onClose={() => setDownloadOpen(false)}
          onConfirm={async (sel) => {
            setDownloadOpen(false);
            await runExport(sel, {
              baseName: safeBase,
              exportSvgId,
              exportRows: exportRowsWithLegend,
            });
          }}
          exportRows={[]}
        />
      )}

      {/* ── Fullscreen modal ── */}
      {showFull && (
        <Modal
          animationType="fade"
          transparent
          visible={showFull}
          onRequestClose={() => setShowFull(false)}
        >
          <View style={fsStyles.overlay}>
            <View style={fsStyles.card}>

              {/* Header */}
              <View style={fsStyles.header}>
                <View style={fsStyles.headerIcon}>
                  <IconButton icon="chart-bell-curve" iconColor="#ffffff" size={22} />
                </View>
                <View style={fsStyles.headerTitles}>
                  <Text style={fsStyles.headerTitle}>{props.ChartTitle}</Text>
                  {props.subTitle ? <Text style={fsStyles.headerSubTitle}>{props.subTitle}</Text> : null}
                </View>
                <View style={fsStyles.headerActions}>
                  <Tooltip title="Close">
                    <IconButton
                      icon="close"
                      iconColor={ChartStyles.chartTitle.color}
                      size={24}
                      onPress={() => setShowFull(false)}
                    />
                  </Tooltip>
                </View>
              </View>

              {/* Legend strip */}
              <View style={fsStyles.legendRow}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ alignItems: "center" }}
                  style={{ flex: 1 }}
                >
                  {legendItems.map((it, idx) => (
                    <View
                      key={`fs-leg-${it.name}-${idx}`}
                      style={{ flexDirection: "row", alignItems: "center", marginHorizontal: 8 }}
                    >
                      <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: it.color, marginRight: 6 }} />
                      <Text style={{ color: ChartStyles.chartTitle.color, fontSize: 12 }} numberOfLines={1}>
                        {it.name}
                      </Text>
                    </View>
                  ))}
                </ScrollView>

                <TouchableOpacity
                  onPress={() => setFullLegendPopoverVisible((v) => !v)}
                  style={{ paddingHorizontal: 6, height: 44, justifyContent: "center" }}
                >
                  <IconButton
                    icon={fullLegendPopoverVisible ? "close-circle-outline" : "format-list-bulleted"}
                    size={20}
                    iconColor={ChartStyles.chartTitle.color}
                    style={{ margin: 0 }}
                  />
                </TouchableOpacity>

                {fullLegendPopoverVisible && (
                  <>
                    <TouchableOpacity
                      style={{ position: "absolute", top: -9999, bottom: -9999, left: -9999, right: -9999, zIndex: 98 }}
                      onPress={() => setFullLegendPopoverVisible(false)}
                      activeOpacity={1}
                    />
                    <View
                      style={{
                        position: "absolute",
                        bottom: 44,
                        right: 8,
                        backgroundColor: "#fff",
                        borderRadius: 8,
                        paddingVertical: 10,
                        paddingHorizontal: 14,
                        shadowColor: "rgba(0,0,0,0.25)",
                        shadowOffset: { width: 0, height: 4 },
                        shadowOpacity: 1,
                        shadowRadius: 10,
                        elevation: 12,
                        zIndex: 99,
                        minWidth: 160,
                        maxHeight: 300,
                      }}
                    >
                      <ScrollView>
                        {legendItems.map((it, idx) => (
                          <View key={`fs-popover-${it.name}-${idx}`} style={{ flexDirection: "row", alignItems: "center", marginVertical: 4 }}>
                            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: it.color, marginRight: 8 }} />
                            <Text style={{ color: ChartStyles.chartTitle.color, fontSize: 12 }}>{it.name}</Text>
                          </View>
                        ))}
                      </ScrollView>
                    </View>
                  </>
                )}
              </View>

              <Divider />

              {/* Chart area — onLayout drives fsWidth; chart only renders once width is known */}
              <View
                style={fsStyles.chartArea}
                onLayout={(e) => setFsWidth(e.nativeEvent.layout.width - 32)}
              >
                {fsChartWidth > 0 && (
                  <ScrollView horizontal showsHorizontalScrollIndicator={fsChartWidth > fsWidth}>
                    <VictoryChart
                      width={fsChartWidth}
                      height={480}
                      padding={{ top: 20, bottom: 70, left: props.yAxisLabel ? 78 : 55, right: 20 }}
                      containerComponent={
                        <VictoryVoronoiContainer
                          labels={({ datum }) => `${datum.x}: ${datum.y} (${datum.key})`}
                        />
                      }
                      domainPadding={{ x: 50, y: 20 }}
                      domain={{ y: [0, fsYMax] }}
                    >
                      <VictoryAxis
                        tickLabelComponent={<VictoryLabel angle={-45} textAnchor="end" />}
                        style={{ tickLabels: { fontSize: 10, padding: 10 } }}
                      />
                      <VictoryAxis
                        dependentAxis
                        label={props.yAxisLabel}
                        style={{ tickLabels: { fontSize: 10, padding: 8 }, axisLabel: { fontSize: 12, padding: 48 } }}
                      />
                      <VictoryGroup style={{ data: { fillOpacity: 0.3 } }}>
                        {transformedDatasetsAll.map((dataset, index) => {
                          const color = resolveColor(props.data?.datasets?.[index]);
                          return (
                            <VictoryArea
                              key={index}
                              interpolation="natural"
                              data={dataset.data}
                              animate={fsAnimate ? { duration: 1500, onLoad: { duration: 1500 } } : false}
                              style={{ data: { stroke: color, fill: color } }}
                            />
                          );
                        })}
                      </VictoryGroup>
                    </VictoryChart>
                  </ScrollView>
                )}
              </View>

            </View>
          </View>
        </Modal>
      )}
    </View>
  );
}
